import React, { useCallback } from 'react';
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
  GridFilterModel,
  DataGridProps,
  ToolbarPropsOverrides,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import { UsersToolbar } from './Toolbar';
import { styled } from '@material-ui/core';

type Props = {
  columns: GridColDef<any>[];
  rows: GridRowParams<any>[];
  loading: boolean;
  onRowClick?: GridEventListener<'rowClick'>;
  title: string;
  create: 'user' | 'organisation';
  isServerSide?: boolean;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  totalRowCount?: number;
  handleSort?: (sortObj: GridSortModel) => void;
  handleFilter?: (filterObj: GridFilterModel) => void;
  paginationModel?: GridPaginationModel;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  sortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
};

const StyledDataGrid = styled(DataGrid)({
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
    outline: 'none',
    '& .MuiDataGrid-columnSeparator': {
      opacity: 1,
    },
  },
});

export const UsersDataGrid = ({
  columns,
  rows,
  loading,
  onRowClick,
  title,
  create,
  onPaginationModelChange,
  totalRowCount,
  handleSort,
  paginationModel,
  isServerSide,
  filterModel,
  onFilterModelChange,
  sortModel,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: Props) => {
  const applyProps = (): Partial<DataGridProps> => {
    if (isServerSide) {
      return {
        paginationMode: 'server',
        filterMode: 'server',
        sortingMode: 'server',
        paginationModel,
        onPaginationModelChange,
        rowCount: totalRowCount,
        sortModel,
        onSortModelChange: handleSort,
        filterModel,
        onFilterModelChange,
      };
    }

    return {
      paginationMode: 'client',
      filterMode: 'client',
      sortingMode: 'client',
      paginationModel,
      onPaginationModelChange,
    };
  };

  const renderToolbar = useCallback(
    (props: ToolbarPropsOverrides) => (
      <UsersToolbar {...props} create={create} />
    ),
    [create],
  );
  return (
    <StyledDataGrid
      {...applyProps()}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      checkboxSelection
      disableRowSelectionOnClick
      columns={columns}
      rows={rows}
      loading={loading}
      pagination
      pageSizeOptions={[20, 50, 100]}
      slots={{
        toolbar: renderToolbar,
      }}
      slotProps={{
        toolbar: {
          title: title,
        },
        // since there's only one filter, operator dropdown is hidden
        filterPanel: {
          filterFormProps: {
            operatorInputProps: {
              sx: { display: 'none' },
            },
          },
        },
      }}
      onRowClick={onRowClick}
      showToolbar
    />
  );
};
